import { useState, useRef, useEffect } from 'react';

/**
 * Dropdown pesquisável — comportamento similar ao filtro do Excel.
 *
 * Props:
 *   options   : [{value, label}]
 *   value     : string (value selecionado)
 *   onChange  : (value, option) => void
 *   placeholder: string (texto quando vazio)
 *   required  : bool
 *   disabled  : bool
 *   className : string extra para o wrapper
 */
export default function SearchableSelect({
  options = [],
  value,
  onChange,
  placeholder = 'Selecione...',
  required = false,
  disabled = false,
  className = '',
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  const selected = options.find(o => o.value === value);

  const filtered = search
    ? options.filter(o => o.label.toLowerCase().includes(search.toLowerCase()))
    : options;

  useEffect(() => {
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
        setSearch('');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    if (open && inputRef.current) inputRef.current.focus();
  }, [open]);

  const handleToggle = () => {
    if (disabled) return;
    setOpen(o => !o);
    setSearch('');
  };

  const handleSelect = (option) => {
    onChange(option.value, option);
    setOpen(false);
    setSearch('');
  };

  return (
    <div ref={containerRef} style={{ position: 'relative' }} className={className}>
      {/* Gatilho — parece um form-select */}
      <div
        className={`form-select d-flex align-items-center gap-2${disabled ? ' disabled' : ''}`}
        style={{ cursor: disabled ? 'not-allowed' : 'pointer', userSelect: 'none', opacity: disabled ? .6 : 1 }}
        onClick={handleToggle}
        role="combobox"
        aria-expanded={open}
        aria-controls="searchable-select-list"
      >
        {selected?.icon && (
          <i className={`bi ${selected.icon} flex-shrink-0`} style={{ color: selected.iconColor }} />
        )}
        <span className={selected ? '' : 'text-muted'} style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {selected ? selected.label : placeholder}
        </span>
      </div>

      {/* Dropdown */}
      {open && (
        <div
          style={{
            position: 'absolute',
            zIndex: 1050,
            top: 'calc(100% + 4px)',
            left: 0,
            right: 0,
            background: '#fff',
            border: '1px solid #dee2e6',
            borderRadius: 8,
            boxShadow: '0 6px 20px rgba(0,0,0,.14)',
          }}
        >
          {/* Campo de pesquisa */}
          <div className="p-2 border-bottom">
            <input
              ref={inputRef}
              className="form-control form-control-sm"
              placeholder="Digite para filtrar..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              onClick={e => e.stopPropagation()}
            />
          </div>

          {/* Lista de opções */}
          <div style={{ maxHeight: 230, overflowY: 'auto' }}>
            {filtered.length === 0 ? (
              <div className="px-3 py-2 text-muted small fst-italic">Nenhum resultado</div>
            ) : (
              filtered.map(o => {
                const isSelected = o.value === value;
                return (
                  <div
                    key={o.value}
                    className="px-3 py-2"
                    style={{
                      cursor: 'pointer',
                      fontSize: '.875rem',
                      fontWeight: isSelected ? 600 : 400,
                      background: isSelected ? '#eef0ff' : 'transparent',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                    }}
                    onMouseEnter={e => { if (!isSelected) e.currentTarget.style.background = '#f4f5ff'; }}
                    onMouseLeave={e => { if (!isSelected) e.currentTarget.style.background = 'transparent'; }}
                    onMouseDown={e => { e.preventDefault(); handleSelect(o); }}
                  >
                    {isSelected
                      ? <i className="bi bi-check2 text-success flex-shrink-0" />
                      : <span style={{ width: 16, flexShrink: 0 }} />}
                    {o.icon && (
                      <i className={`bi ${o.icon} flex-shrink-0`} style={{ color: o.iconColor }} />
                    )}
                    {o.label}
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}

      {/* Input invisível para validação nativa do form */}
      {required && (
        <input
          tabIndex={-1}
          required
          value={value || ''}
          onChange={() => {}}
          style={{ opacity: 0, height: 0, position: 'absolute', pointerEvents: 'none' }}
        />
      )}
    </div>
  );
}
